import React, { useEffect, useMemo, useState } from "react";
import { getAdminAnalysisErrors } from "../../api/admin.api";
import { isUnauthorizedError } from "../../api/http";
import AdminBreadcrumbs from "../../components/admin/AdminBreadcrumbs";
import EmptyState from "../../components/ui/EmptyState";
import ErrorState from "../../components/ui/ErrorState";
import LoadingState from "../../components/ui/LoadingState";
import AdminLayout from "../../layouts/AdminLayout";
import AdminBlockingModal from "./AdminBlockingModal";
import { handleTableKeyNav } from "../../utils/tableNav";
import normalizeApiError from "../../utils/normalizeApiError";

const formatTimestamp = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const pad = (num) => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const statusTone = (value) => {
  const status = String(value || "").toLowerCase();
  if (status === "resolved" || status === "fixed")
    return "bg-emerald-100 text-emerald-700";
  if (status === "open" || status === "active")
    return "bg-amber-100 text-amber-700";
  if (status === "failed" || status === "error")
    return "bg-rose-100 text-rose-700";
  return "bg-slate-100 text-slate-500";
};

export default function AdminAnalysisErrors() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blockingError, setBlockingError] = useState("");

  const canGoPrev = page > 1;
  const canGoNext = total ? page * pageSize < total : items.length === pageSize;

  const fetchErrors = async (nextPage = page) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAdminAnalysisErrors({ page: nextPage, pageSize });
      setItems(Array.isArray(response?.items) ? response.items : []);
      setPage(response?.page || nextPage);
      setTotal(response?.total ?? null);
    } catch (err) {
      if (isUnauthorizedError(err)) {
        setError(normalizeApiError(err));
        return;
      }
      const code = err?.response?.data?.code;
      if (code === "BILLING_CREDITS_EXHAUSTED") {
        setBlockingError(err?.response?.data?.message || "Credits exhausted.");
        return;
      }
      setError(normalizeApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchErrors(1);
  }, []);

  const displayItems = useMemo(() => items, [items]);

  return (
    <AdminLayout title="Admin Analysis Errors">
      <div className="space-y-6">
        <div className="card p-4">
          <AdminBreadcrumbs />
        </div>
        {loading ? (
          <LoadingState variant="table" rows={6} />
        ) : error ? (
          <ErrorState error={error} onRetry={fetchErrors} />
        ) : displayItems.length === 0 ? (
          <EmptyState
            title="No errors recorded"
            message="There are no analysis errors to display."
            testId="admin-empty-analysis-errors"
          />
        ) : (
          <>
            <div
              className="hidden overflow-hidden rounded-lg border border-slate-200 lg:block"
              onKeyDown={handleTableKeyNav}
            >
              <table
                className="w-full text-left text-sm"
                role="table"
                aria-label="Analysis errors"
              >
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">Code</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Message</th>
                    <th className="px-4 py-3">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.map((item, index) => (
                    <tr
                      key={item.id || index}
                      className="border-t border-slate-100 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-amber-400"
                      tabIndex={0}
                      data-row="true"
                    >
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {item.id || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {item.code || item.errorCode || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold ${statusTone(
                            item.status
                          )}`}
                        >
                          {item.status || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {item.message || item.error || "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {formatTimestamp(item.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="space-y-3 lg:hidden">
              {displayItems.map((item, index) => (
                <div key={item.id || index} className="card p-4 text-xs text-slate-600">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-900">
                      {item.code || item.errorCode || "Error"}
                    </div>
                    <span
                      className={`rounded-full px-2 py-1 text-[10px] font-semibold ${statusTone(
                        item.status
                      )}`}
                    >
                      {item.status || "—"}
                    </span>
                  </div>
                  <div className="mt-2 grid gap-2 text-slate-600">
                    <div>ID: {item.id || "—"}</div>
                    <div>Message: {item.message || item.error || "—"}</div>
                    <div>Created: {formatTimestamp(item.createdAt)}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <div className="flex gap-2">
          <button
            className="btn btn-secondary"
            onClick={() => fetchErrors(page - 1)}
            disabled={!canGoPrev || loading}
          >
            Prev
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => fetchErrors(page + 1)}
            disabled={!canGoNext || loading}
          >
            Next
          </button>
        </div>
      </div>
      <AdminBlockingModal
        title="Billing credits exhausted"
        message={blockingError}
        onClose={() => setBlockingError("")}
      />
    </AdminLayout>
  );
}
